/**
 * Horizontal-trace handling for the Circular tunnel.
 *
 * A discontinuity trace plots horizontal (i.e. with infinite persistence along
 * the tunnel) whenever the apparent dip is 0. That happens in two situations:
 *
 *   1. The dip amount is 0 (a flat-lying plane).
 *   2. The strike is parallel to the tunnel direction, i.e. Alpha == 0
 *      (the dip direction is perpendicular to the tunnel direction).
 *
 * In both cases the trace cannot be drawn (it has no slope / the geometry
 * divides by zero), so we revise the inputs slightly to the 1st decimal place
 * and surface an alert. The user-entered values are kept untouched for display
 * in the Input table — only the values fed into the plotting math are revised.
 *
 * Mirrors dshape/utils/horizontalTrace.js (tunnel-agnostic logic).
 */

// Angle between the strike and the tunnel direction. Mirrors calculateAlpha in
// the renderers so detection uses exactly the same definition.
const alphaBetween = (D, Dir) => {
  const Dnew = D > 180 ? D - 180 : D;
  const Dirnew = Dir > 180 ? Dir - 180 : Dir;
  const Value = Math.abs(Dnew - Dirnew);
  return Value <= 90 ? 90 - Value : Value - 90;
};

export const HORIZONTAL_ALERT_STRIKE =
  "Alert: Discontinuity trace horizontal as discontinuity strike parallel to tunnel direction.";

export const HORIZONTAL_ALERT_FLAT =
  "Alert: Discontinuity trace horizontal.";

export const HORIZONTAL_ALERT_INCLINED =
  "Alert: Discontinuity trace horizontal as discontinuity strike parallel to tunnel direction and dip amount equal to tunnel inclination.";

/**
 * Detect a horizontal trace and revise Dir / amt so a finite trace can be drawn.
 *
 * @param {number} D    Tunnel direction (integer degrees)
 * @param {number} Dir  Dip direction (integer degrees)
 * @param {number} amt  Dip amount (integer degrees)
 * @returns {{ D: number, Dir: number, amt: number, alert: ?string }}
 *          The revised values plus an alert message (null when no change needed).
 */
export const applyHorizontalTrace = (D, Dir, amt, inclination = 0) => {
  // A 360° input means 0°.
  if (D === 360) D = 0;
  if (Dir === 360) Dir = 0;

  const flat = amt === 0;
  const strikeParallel = alphaBetween(D, Dir) === 0;
  // Inclined tunnel: dip direction along the tunnel AND dip amount equal to the
  // tunnel inclination ⇒ the discontinuity plane contains the tunnel axis, so the
  // apparent dip along the axis (Beta) is 0 (TanBeta → 0, infinite persistence).
  const incl = Number(inclination) || 0;
  const inclinedParallel = incl > 0 && Dir === D && amt === incl;

  // Flat-lying plane: nudge the dip amount off zero.
  if (flat) {
    amt = 0.1;
  }

  // Strike parallel to the tunnel: nudge the dip direction by 0.1°.
  if (strikeParallel) {
    if (D >= 90 && D <= 270) {
      // Case 1
      if (D === 270 && Dir === 0) {
        Dir = 359.9;
      } else if (Dir > D) {
        Dir = Dir - 0.1;
      } else if (Dir < D) {
        Dir = Dir + 0.1;
      }
    } else if (D < 90) {
      // Case 2
      Dir = Dir >= 270 ? Dir + 0.1 : Dir - 0.1;
    } else {
      // Case 3: D > 270
      Dir = Dir <= 90 ? Dir - 0.1 : Dir + 0.1;
    }
  }

  // Inclined-parallel: nudge the dip amount off the tunnel inclination so the
  // apparent dip along the axis is a small non-zero value.
  if (inclinedParallel) {
    amt = amt - 0.01;
  }

  // Precedence: inclined-parallel (most specific) → flat → strike-parallel.
  let alert = null;
  if (inclinedParallel) {
    alert = HORIZONTAL_ALERT_INCLINED;
  } else if (flat) {
    alert = HORIZONTAL_ALERT_FLAT;
  } else if (strikeParallel) {
    alert = HORIZONTAL_ALERT_STRIKE;
  }

  return { D, Dir, amt, alert };
};
